<template>
  <v-card
    class="rounded-lg"
  >
    <slot name="header" />

    <v-card-text>
      <apexchart
        :type="type"
        :height="height"
        :options="chartOptions"
        :series="series"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { get } from 'lodash'
import { ApexChartsLocale } from '@/data/models/ApexchartsLocale'
import i18n from '@/plugins/i18n'

export default {
  name: 'LineChart',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    rawData: {
      type: Array,
      default: () => ([])
    },
    type: {
      type: String,
      default: 'line'
    },
    chartValueKeys: {
      type: Array,
      default: () => ([])
    },
    xValue: {
      type: String,
      default: undefined
    },
    yValuePrefix: {
      type: String,
      default: ''
    },
    hideLabels: {
      type: Boolean,
      default: false
    },
    xaxis: {
      type: Object,
      default: () => ({
        type: 'datetime',
        labels: {
          format: 'ddd, dd.MM.'
        }
      })
    },
    yaxis: {
      type: Array,
      default: () => []
    },
    height: {
      type: Number,
      default: 350
    },
    colors: {
      type: Array,
      default: () => [
        '#008FFB',
        '#00E396',
        '#FEB019',
        '#FF4560',
        '#775DD0',
        '#af754c'
      ]
    },
    annotations: {
      type: Object,
      default: () => ({})
    },
    plotOptions: {
      type: Object,
      default: () => ({
        bar: {
          borderRadius: 5,
          columnWidth: '50%'
        }
      })
    },
    fill: {
      type: Object,
      default: () => ({
        type: 'solid',
        opacity: [1, 0.35]
      })
    },
    stroke: {
      type: Object,
      default: () => ({
        curve: 'smooth',
        width: 3
      })
    },
    tooltip: {
      type: Object,
      default: () => ({
        x: {
          format: 'dd.MM.yyyy.'
        }
      })
    },
    markers: {
      type: Object,
      default: () => ({})
    },
    seriesOverride: {
      type: Array,
      default: () => undefined
    },
    grid: {
      type: Object,
      default: () => ({})
    },
    stacked: {
      type: Boolean,
      default: false
    },
    dataLabels: {
      type: Object,
      default: () => ({ enabled: false })
    },
    events: {
      type: Function,
      default: () => null
    },
    animations: {
      type: Object,
      default: () => ({})
    },
    legend: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      series: [],
      labels: []
    }
  },
  computed: {
    chartOptions() {
      return {
        colors: this.colors,
        chart: {
          height: this.height,
          stacked: this.stacked,
          options: this.options,
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          },
          events: {
            dataPointSelection: this.events
          },
          animations: this.animations,
          locales: ApexChartsLocale,
          defaultLocale: i18n.locale
        },
        dataLabels: this.dataLabels,
        grid: this.grid,
        labels: this.labels,
        xaxis: this.xaxis,
        yaxis: this.yaxis,
        annotations: this.annotations,
        tooltip: this.tooltip,
        plotOptions: this.plotOptions,
        stroke: this.stroke,
        fill: this.fill,
        markers: this.markers,
        legend: this.legend
      }
    }
  },
  watch: {
    rawData() {
      this.processRawData()
    },
    chartValueKeys() {
      this.processRawData()
    },
    seriesOverride() {
      this.processRawData()
    }
  },
  created() {
    this.processRawData()
  },
  methods: {
    processRawData() {
      if (this.seriesOverride) {
        this.series = this.seriesOverride
        this.labels = []
        return
      }

      const series = []
      const labels = []

      for (const stat of this.rawData) {
        labels.push(get(stat, this.xValue))
      }

      this.labels = labels

      for (const key of this.chartValueKeys) {
        if (key.key) {
          series.push({
            type: key.type || 'line',
            name: key.label || key.key,
            data: this.rawData.map(stat => {
              return get(stat, this.yValuePrefix + '[' + key.key + ']', ('default' in key) ? key.default : null)
            })
          })
        } else {
          series.push({
            type: 'line',
            name: key,
            data: this.rawData.map(stat => {
              return get(stat, this.yValuePrefix + key)
            })
          })
        }
      }

      this.series = series
    }
  }
}
</script>
